import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
// TODO: Add react-helmet-async when React 19 support is available for SEO meta tags
import Icon from "@mdi/react";
import { mdiDanceBallroom, mdiTicket, mdiSchool, mdiAccountCash, mdiEmailMultipleOutline, mdiCog, mdiClipboardTextOutline, mdiCloudUploadOutline } from "@mdi/js";

import AppHeader from "../components/AppHeader";
import OnboardingChecklistModal from "../components/OnboardingChecklistModal";
import { organizationApi } from "../api/organization";
import { useAuth } from "../contexts/AuthContext";
import { useDevMode } from "../hooks/useDevMode";
import { useOnboardingChecklist } from "../hooks/useOnboardingChecklist";
import { cacheImage, getCachedImage, base64ToBlobUrl } from "../utils/imageCache";

import "../pages/header.css";
import "../pages/Common.css"; /* Common styles for all pages */
import "../pages/Dashboard.css";
import "../pages/home.css";

import danceLocaleIcon from "../assets/dance_locale_3-200h.png";

const Dashboard = () => {
    const { user, checkPermission, organisation, organisationLoading, refreshOrganisation, checkFeature } = useAuth();
    const { isDevMode } = useDevMode();
    const navigate = useNavigate();

    // Setup checklist. Called here (not inside the modal) so the icon badge and the
    // modal share one fetch and can never disagree on what is still outstanding.
    const onboardingChecklist = useOnboardingChecklist();

    // Brand-new org detection. settings.features is now written at create time from the tools
    // chosen at registration, so its absence is no longer the signal; settings.onboardingChecklistSeen
    // is written false in the same insert and serves instead.
    // STRICT === false on purpose: the signal is presence-of-false, never absence. Orgs created
    // before this change carry neither the key nor false, so they correctly never auto-open.
    // Writing `!seen` or `seen !== true` here would fire the modal for every pre-existing org on
    // every dashboard load. Guarded by organisationLoading so it never flashes before the org loads.
    const needsOnboardingChecklist =
        !organisationLoading && !!organisation && organisation.settings?.onboardingChecklistSeen === false;

    // Who can see the Uploads (ICS import) action. Gated on the deliberate
    // per-account permissions.canUploadEvents flag on the JWT only.
    // Legacy org-role fallback (owner/admin of the org-context session) is parked
    // below — Uploads should surface only on an explicit flag, not by org role.
    // If re-enabled, mirror it in the /uploads route guard in ProtectedRoute.js.
    const canShowUploads =
        user?.permissions?.canUploadEvents === true;
        // || (user?.loginType === "orgContext" && (user?.role === "owner" || user?.role === "admin"));

    // State management
    const [schoolInfo, setSchoolInfo] = useState({
        orgName: "",
        businessAddress: "Address not set",
    });
    const [orgData, setOrgData] = useState(null);
    const [orgSettings, setOrgSettings] = useState(() => {
        const cached = localStorage.getItem('dl_dashboardPrimaryCard');
        return cached !== null ? { dashboardPrimaryCard: cached === 'null' ? null : cached } : null;
    });

    // "Finish setting up" onboarding checklist. Opens automatically once for a brand-new org
    // (see needsOnboardingChecklist), and is always reachable from the checklist icon beside the cog.
    const [showOnboardingChecklist, setShowOnboardingChecklist] = useState(false);

    // Profile overlay (avatar/logo) image state
    const [profileOverlayImageUrl, setProfileOverlayImageUrl] = useState(null);
    const [, setIsLoadingProfileOverlayImage] = useState(false);
    const [isShowingOverlayPlaceholder, setIsShowingOverlayPlaceholder] = useState(true);

    // Renders one dashboard module card. A feature that is toggled OFF renders GREYED OUT
    // and non-clickable (kept on screen so the org can see what is available to enable),
    // and is never the hero card. HERO_TO_FEATURE maps a dashboardPrimaryCard value to its
    // feature key so a disabled feature can never be the featured/enlarged card.
    const HERO_TO_FEATURE = { classes: 'classManagement', privateLessons: 'privateLessons', ticketing: 'ticketing' };

    const renderModuleCard = ({ to, feature, heroValue, heroCard, title, iconPath, subtitle }) => {
        const enabled = !feature || checkFeature(feature);
        const isHero = enabled && heroValue && heroCard === heroValue;
        const cardClass = isHero
            ? 'module-card-hero'
            : (enabled ? 'module-card' : 'module-card module-card-disabled');
        const textColor = enabled ? 'var(--dl-color-purple-default)' : 'var(--dl-color-gray-500)';
        const inner = (
            <>
                {/* Enable pill is an absolute OVERLAY (not in the header flow) so it never
                    squeezes/wraps the title. Only on disabled cards; routes to Settings. */}
                {!enabled && (
                    <button
                        type="button"
                        className="module-card-enable-pill"
                        onClick={() => navigate('/settings')}
                    >
                        Enable
                    </button>
                )}
                <div className="module-card-header">
                    <h3 style={{ color: textColor, fontSize: isHero ? undefined : "1.25rem", margin: 0 }}>{title}</h3>
                    {enabled && <Icon path={iconPath} size={isHero ? 1.4 : 1.1} color={textColor} />}
                </div>
                <p className="module-card-subtitle">{subtitle}</p>
            </>
        );
        return enabled
            ? <Link to={to} className={cardClass}>{inner}</Link>
            : <div className={cardClass} aria-disabled="true">{inner}</div>;
    };

    const renderModuleGrid = () => {
        // A disabled feature is greyed (not hidden), so the top row always has its 3 cards.
        // The hero only applies when its mapped feature is ON (the server also nulls a hero
        // pointing at a disabled feature; this guards the race window before refresh).
        const rawHero = orgSettings?.dashboardPrimaryCard || null;
        const heroCard = rawHero && checkFeature(HERO_TO_FEATURE[rawHero]) ? rawHero : null;

        const heroRowVariant =
            heroCard === 'classes' ? ' module-grid-row--hero-left' :
            heroCard === 'privateLessons' ? ' module-grid-row--hero-middle' :
            heroCard === 'ticketing' ? ' module-grid-row--hero-right' : '';

        // Second row: Email Students greys when bulkEmail is off (always rendered); Manage
        // Finances is permission-gated and may be absent. Centre when only one card shows.
        const secondRowCount = 1 + (checkPermission('viewFinancialReports') ? 1 : 0);
        const secondRowClass = secondRowCount >= 2
            ? "module-grid-row-2col module-grid-row--left-third module-grid-row--no-stretch"
            : "module-grid-row module-grid-row--centered module-grid-row--no-stretch";

        return (
            <div className="tab-content-overview">
                {/* Top row — Classes, Private Lessons, Ticketing */}
                <div className={`module-grid-row module-grid-row--no-stretch${heroRowVariant}`}>
                    {renderModuleCard({ to: "/classes", feature: "classManagement", heroValue: "classes", heroCard, title: "Classes", iconPath: mdiSchool, subtitle: "Manage classes, enrolments and check-in" })}
                    {renderModuleCard({ to: "/instructor-dashboard", feature: "privateLessons", heroValue: "privateLessons", heroCard, title: "Private Lessons", iconPath: mdiDanceBallroom, subtitle: "Manage private lessons and booking confirmations" })}
                    {renderModuleCard({ to: "/ticketing", feature: "ticketing", heroValue: "ticketing", heroCard, title: "Ticketing", iconPath: mdiTicket, subtitle: "Create and manage events and ticket sales" })}
                </div>

                <hr className="module-grid-divider" />

                {/* Second row — Send Emails + Manage Finances */}
                <div className={secondRowClass}>
                    {renderModuleCard({ to: "/communications", feature: "bulkEmail", title: "Email Students", iconPath: mdiEmailMultipleOutline, subtitle: "Compose new emails or review what's already gone out" })}
                    {checkPermission('viewFinancialReports') && renderModuleCard({ to: "/manage-finances", title: "Manage Finances", iconPath: mdiAccountCash, subtitle: "Earnings, reporting and bank reconciliation" })}
                </div>

            </div>
        );
    };


    const loadOrganisationData = useCallback(async () => {
        try {
            const token = localStorage.getItem("accessToken");
            if (token) {
                const payload = JSON.parse(atob(token.split(".")[1]));
                if (payload.organisationId) {
                    const [orgData, settings] = await Promise.all([
                        organizationApi.getOrgProfile(payload.organisationId),
                        organizationApi.getSettings(payload.organisationId).catch(() => null),
                    ]);

                    if (orgData) {
                        setOrgData(orgData);

                        setSchoolInfo({
                            orgName: orgData.name || "Your name here",
                            businessAddress: orgData.orgLocation?.description || "Address not set",
                        });
                    }

                    if (settings) {
                        setOrgSettings(settings);
                        localStorage.setItem('dl_dashboardPrimaryCard', settings.dashboardPrimaryCard ?? 'null');
                    }
                }
            }
        } catch (error) {
            console.error("Error loading organisation data:", error);
        }
    }, []);

    // Fetch organization overlay image (business logo)
    const fetchProfileOverlayImage = useCallback(async () => {
        const abortController = new AbortController();

        const imageKey = orgSettings?.orgImageKey || orgData?.orgImageKey || null;
        const orgLogoVersion = orgSettings?.orgLogoVersion || orgData?.orgLogoVersion;
        const cacheKey = `${imageKey}_v${orgLogoVersion}`;

        // console.log('[IMG-DEBUG] fetchProfileOverlayImage triggered', { imageKey, orgLogoVersion, cacheKey, hasOrgData: !!orgData, hasOrgSettings: !!orgSettings });

        if (!imageKey) {
            // No logo image — keep the fox placeholder visible instead of falling back to blank avatar
            // console.log('[IMG-DEBUG] fetchProfileOverlayImage: no imageKey yet, skipping');
            return abortController;
        }

        try {
            setIsLoadingProfileOverlayImage(true);
            setIsShowingOverlayPlaceholder(true);

            const cachedUrl = getCachedImage(cacheKey, "logo");
            if (cachedUrl) {
                // console.log('[IMG-DEBUG] fetchProfileOverlayImage: serving from cache');
                setProfileOverlayImageUrl(base64ToBlobUrl(cachedUrl));
                setIsLoadingProfileOverlayImage(false);
                setIsShowingOverlayPlaceholder(false);
                return abortController;
            }

            // console.log('[IMG-DEBUG] fetchProfileOverlayImage: fetching from API', { imageKey });
            const response = await organizationApi.getProfileImageBase64(imageKey, { timeout: 30000 });

            if (abortController.signal.aborted) {
                // console.log('[IMG-DEBUG] fetchProfileOverlayImage: aborted after API call');
                return;
            }

            // console.log('[IMG-DEBUG] fetchProfileOverlayImage: API response', { ok: response.ok, hasImageBase64: !!response.data?.imageBase64, status: response.status });

            if (response.ok && response.data.imageBase64) {
                const base64DataUrl = response.data.imageBase64;
                setProfileOverlayImageUrl(base64ToBlobUrl(base64DataUrl));

                cacheImage(cacheKey, base64DataUrl, "logo").catch(err =>
                    console.warn("Failed to cache overlay image:", err)
                );
            } else {
                // console.warn('[IMG-DEBUG] fetchProfileOverlayImage: API returned no image data', { ok: response.ok, data: response.data });
            }
        } catch (error) {
            // console.error('[IMG-DEBUG] fetchProfileOverlayImage: error', error);
        } finally {
            if (!abortController.signal.aborted) {
                setIsLoadingProfileOverlayImage(false);
                setIsShowingOverlayPlaceholder(false);
            }
        }

        return abortController;
    }, [orgSettings?.orgImageKey, orgData?.orgImageKey, orgSettings?.orgLogoVersion, orgData?.orgLogoVersion]);

    // Initial load - load basic org info
    useEffect(() => {
        loadOrganisationData();
    }, [loadOrganisationData]);

    // Open the setup checklist once for a brand-new org. The dismissal is persisted in the
    // modal's onClose, and refreshOrganisation then clears this condition, so it cannot loop.
    useEffect(() => {
        if (needsOnboardingChecklist) {
            setShowOnboardingChecklist(true);
        }
    }, [needsOnboardingChecklist]);

    // Load logo overlay image when org data or settings are available
    useEffect(() => {
        let overlayAbortController;

        if (orgData || orgSettings) {
            fetchProfileOverlayImage().then(controller => {
                overlayAbortController = controller;
            });
        }

        return () => {
            overlayAbortController?.abort();
        };
    }, [orgData, orgSettings, fetchProfileOverlayImage]);


    // Render the main dashboard
    return (
        <div className="dashboard-container org-console">
            {/* TODO: Add SEO meta tags when react-helmet-async supports React 19
                Title: "Dashboard - Dance Localé"
                OG Title: "Dashboard - Dance Localé" */}

            <OnboardingChecklistModal
                isOpen={showOnboardingChecklist}
                onClose={async () => {
                    setShowOnboardingChecklist(false);
                    // Persist the dismissal so it does not reopen on the next load. Only on the
                    // first-run path — a user who opened the checklist from the header icon is
                    // not dismissing anything.
                    if (needsOnboardingChecklist) {
                        try {
                            await organizationApi.updateOnboardingSeen(user.organisationId);
                            await refreshOrganisation();
                        } catch (err) {
                            // Non-fatal: the checklist is a nudge, not a gate. Worst case it
                            // reappears next load, which beats blocking the dashboard.
                            console.error('Failed to persist onboarding-seen:', err);
                        }
                    }
                }}
                groups={onboardingChecklist.groups}
                hasStatus={onboardingChecklist.hasStatus}
                totalCount={onboardingChecklist.totalCount}
                completeCount={onboardingChecklist.completeCount}
            />

            <AppHeader activeItem="dashboard" />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    {/* Dashboard Header Card */}
                    <div className="dashboard-header-card">
                        <div className="dashboard-header-grid">
                            {/* Left Column - School Info */}
                            <div className="dashboard-header-left">
                                <div className="dashboard-school-details">
                                    <div className="school-info-with-avatar">
                                        {/* Profile Avatar - left of studio name */}
                                        <div className="dashboard-profile-overlay-header">
                                            {isShowingOverlayPlaceholder ? (
                                                <img
                                                    src={danceLocaleIcon}
                                                    alt="Dance Locale Mascot"
                                                    className="dashboard-profile-overlay-img"
                                                />
                                            ) : profileOverlayImageUrl ? (
                                                <img
                                                    src={profileOverlayImageUrl}
                                                    alt="Profile"
                                                    className="dashboard-profile-overlay-img"
                                                    onError={e => {
                                                        e.target.onerror = null;
                                                        e.target.src = danceLocaleIcon;
                                                    }}
                                                />
                                            ) : (
                                                <img
                                                    src={danceLocaleIcon}
                                                    alt="Dance Locale Mascot"
                                                    className="dashboard-profile-overlay-img"
                                                />
                                            )}
                                        </div>
                                        <div className="school-text">
                                            <h2 className="page-title">{schoolInfo.orgName}</h2>
                                            <p style={{ margin: "0", fontSize: "14px", color: "#666" }}>
                                                Dashboard
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* TODO: Dashboard header metrics — applicable metrics TBA, wire up when decided
                                <div className="dashboard-today-summary">
                                    <span className="today-summary-label">Today</span>
                                    <span className="today-summary-stat">3 classes</span>
                                    <span className="today-summary-divider">·</span>
                                    <span className="today-summary-stat">28 students expected</span>
                                    <span className="today-summary-divider">·</span>
                                    <span className="today-summary-stat">4 private lessons</span>
                                </div>
                                */}

                                {/* Dev pill + Uploads action share one row. Two distinct session
                                    shapes can upload events:
                                      - Direct login (loginType "owner"): personal JWT flag
                                        permissions.canUploadEvents, set on the auth doc.
                                      - Org-context (loginType "orgContext", arrived via the org
                                        selector): canUploadEvents is HARDCODED false in that JWT, so
                                        gate on the org role instead (owner/admin of the selected org).
                                    Mirror this same condition in the /uploads route guard. */}
                                {(isDevMode || canShowUploads) && (
                                    <div className="dashboard-header-utility-row">
                                        {/* Environment Status - Shows current environment (auto-detected) */}
                                        {isDevMode && (
                                            <div className="dev-mode-indicator">
                                                <span className="dev-icon">🧪</span>
                                                <span className="dev-text">DEVELOPMENT DATABASE</span>
                                                <span className="dev-hostname">localhost</span>
                                            </div>
                                        )}

                                        {canShowUploads && (
                                            <button
                                                type="button"
                                                className="dashboard-header-uploads-btn"
                                                onClick={() => navigate('/uploads')}
                                                title="Upload events"
                                            >
                                                <Icon path={mdiCloudUploadOutline} size={0.8} color="var(--dl-color-purple-default)" />
                                                <span>Uploads</span>
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Team member role indicator — bottom-left, aligns with cover image bottom */}
                                {user?.loginType === "orgContext" && user?.role && (
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                        {(user.accountDetails?.firstName || user.username || user.email) && (
                                            <span style={{ fontSize: "13px", color: "#666" }}>
                                                {user.accountDetails?.firstName || user.username || user.email}
                                            </span>
                                        )}
                                        <span style={{ fontSize: "13px", color: "#999" }}>logged in as</span>
                                        <span className={`settings-role-badge settings-role-badge--${user.role}`}>
                                            {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Right Column - Setup checklist + Settings Gear */}
                            <div className="dashboard-header-right">
                                {/* Setup checklist. The badge counts only genuinely
                                    outstanding steps: pre-filled cards (standard terms,
                                    already working) are excluded, so the number stays an
                                    honest "things left to do". Hidden at zero, and until
                                    the status resolves. */}
                                <button
                                    className="dashboard-header-settings-btn dashboard-header-checklist-btn"
                                    onClick={() => setShowOnboardingChecklist(true)}
                                    aria-label={
                                        onboardingChecklist.incompleteCount > 0
                                            ? `Complete your setup checklist, ${onboardingChecklist.incompleteCount} steps still to do`
                                            : "Complete your setup checklist"
                                    }
                                    title="Complete your setup checklist"
                                >
                                    <Icon path={mdiClipboardTextOutline} size={1.1} color="var(--dl-color-purple-default)" />
                                    {onboardingChecklist.incompleteCount > 0 && (
                                        <span className="dashboard-checklist-badge" aria-hidden="true">
                                            {onboardingChecklist.incompleteCount}
                                        </span>
                                    )}
                                </button>
                                <button
                                    className="dashboard-header-settings-btn"
                                    onClick={() => navigate('/settings')}
                                    aria-label="Settings"
                                    title="Settings"
                                >
                                    <Icon path={mdiCog} size={1.1} color="var(--dl-color-purple-default)" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Module Grid */}
                    <div className="dashboard-module-content">{renderModuleGrid()}</div>
                </div>
            </div>

            {/* Footer — matches homepage footer */}
            <div className="home-footer">
                <footer className="home-container22">
                    <div className="home-container23">
                        <span className="home-text56" onClick={() => navigate('/privacy-policy')} style={{ cursor: 'pointer' }}>
                            Privacy policy
                        </span>
                        <span className="home-text57" onClick={() => navigate('/terms-conditions')} style={{ cursor: 'pointer' }}>
                            Terms of Service
                        </span>
                        <span className="home-text58" onClick={() => navigate('/contact')} style={{ cursor: 'pointer' }}>
                            Contact
                        </span>
                    </div>
                    <div className="home-divider"></div>
                    <div className="home-container24">
                        <span className="home-text59 Body2">© {new Date().getFullYear()} Dance Localé. All rights reserved</span>
                    </div>
                </footer>
            </div>
        </div>
    );
};

/*
 * ARCHIVED: Event Calendar Section (Removed 2026-02-14)
 *
 * Reason: Reducing dashboard clutter for beta users
 * Future: May reintegrate into Ticketing module
 * Location: See git history for original implementation
 */

export default Dashboard;
